import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ExternalLink, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = () => {
    try {
      const stored = localStorage.getItem('rv_favorites');
      const favs = stored ? JSON.parse(stored) : [];
      setFavorites(favs);
    } catch (err) {
      console.error('Error loading favorites:', err);
      setFavorites([]);
    }
    setLoading(false);
  };

  const removeFavorite = (rvId) => {
    const updated = favorites.filter(fav => fav.id !== rvId);
    localStorage.setItem('rv_favorites', JSON.stringify(updated));
    setFavorites(updated);
    toast.success('Removed from favorites');
  };

  const clearAll = () => {
    localStorage.removeItem('rv_favorites');
    setFavorites([]);
    toast.success('All favorites cleared');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
              </Link>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Heart className="text-red-500" size={20} />
                  My Favorites
                </h1>
                <p className="text-xs text-gray-500">{favorites.length} saved RVs</p>
              </div>
            </div>
            {favorites.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearAll}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                Clear All
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={32} className="text-gray-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No favorites yet
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
              When you find RVs you like, tap the heart icon to save them here for easy comparison.
            </p>
            <Link to="/quiz">
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Sparkles size={16} className="mr-2" />
                Find Your Match
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {favorites.map((rv) => (
              <div 
                key={rv.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
              >
                <div className="flex">
                  {/* Image */}
                  <div className="w-28 h-28 sm:w-36 sm:h-36 shrink-0 bg-gray-100 dark:bg-gray-700">
                    {rv.image ? (
                      <img 
                        src={rv.image} 
                        alt={rv.name || rv.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Sparkles size={24} />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-3 sm:p-4 flex flex-col">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base line-clamp-1">
                        {rv.name || rv.title || 'Unknown RV'}
                      </h3>
                      {rv.year && rv.make && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {rv.year} {rv.make}
                        </p>
                      )}
                      {rv.price && (
                        <p className="text-orange-500 font-bold mt-1 text-sm sm:text-base">
                          ${typeof rv.price === 'number' ? rv.price.toLocaleString() : rv.price}
                        </p>
                      )}
                      {rv.savedAt && (
                        <p className="text-xs text-gray-400 mt-1">
                          Saved {new Date(rv.savedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2">
                      {rv.url && (
                        <a 
                          href={rv.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button size="sm" variant="outline" className="w-full h-8 text-xs">
                            <ExternalLink size={14} className="mr-1" />
                            View Details
                          </Button>
                        </a>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => removeFavorite(rv.id)}
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
